"use client";

import { useApiGet } from "@/hooks/use-api";
import { integrationKeys } from "./keys";
import type { GmailConnectionStatus } from "./types";

/**
 * useGmailStatus
 *
 * Returns the Gmail connection status for the current workspace.
 * The backend reads workspaceId from the x-workspace-id auth header
 * (injected by api-helpers.ts), so it does not appear in the URL.
 * The `workspaceId` param is used only to scope the cache key and to
 * gate the fetch until the workspace is known.
 *
 * Usage:
 *   const { connected, userEmail, isLoading } = useGmailStatus(workspaceId);
 */
export function useGmailStatus(workspaceId: string | undefined) {
  const safeId = workspaceId ?? "";

  const { data, isLoading, isError, refetch } = useApiGet<GmailConnectionStatus>(
    integrationKeys.gmail(safeId),
    "/api/gmail/status",
    undefined,
    {
      enabled: !!safeId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  return {
    connected: data?.connected ?? false,
    userEmail: data?.userEmail,
    hasWriteAccess: data?.hasWriteAccess,
    isLoading,
    isError,
    refetch,
  };
}
